import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import { CognitoAccessToken, CognitoUserSession } from 'amazon-cognito-identity-js';
import { VoidFunction } from '../../../types';
import {AuthFlowType, CognitoIdentityProviderClient, ConfirmSignUpCommand, InitiateAuthCommand, RespondToAuthChallengeCommand, SignUpCommand } from '@aws-sdk/client-cognito-identity-provider';

interface AmazonCognitoProvider {
  isAuthenticated: boolean;
  cognitoUser: AmazonCognitoIdentity.CognitoUser | null;
  signin(_username: string, _password: string, _callback: any): void;
  signout(_callback: VoidFunction): void;
  signup(_username: string, _password: string, _callback: any): void;
  forgotPassword(_username: string, _callback: any): void;
  confirmPassword(_username: string, _verificationCode: string, _newPassword: string, _callback: any): void;
  verify(_username: string, _code: string, _session: string, _callback: any): void;
  signinAfterVerify(_username: string, _password: string, _session: string, _callback: any): void;
  resendVerificationCode(_username: string, _callback: any): void;
  getAccessToken(): Promise<CognitoAccessToken | null>;
  getSession(): Promise<CognitoUserSession | null>;
  changePassword(_oldPassword: string, _newPassword: string, _callback: (success: boolean, error?: string) => void): void;
}

const cognitoUserPoolId = process.env.REACT_APP_AMAZON_COGNITO_USER_POOL_ID!;
const cognitoClientId = process.env.REACT_APP_AMAZON_COGNITO_CLIENT_ID!;

export const poolData = {
  UserPoolId: cognitoUserPoolId,
  ClientId: cognitoClientId,
};


const CognitoProvider: AmazonCognitoProvider = {
  isAuthenticated: false,
  cognitoUser: null,

  signin(username: string, password: string, callback: any) {
    var authenticationData = {
      Username: username,
      Password: password,
    };
    var authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(
      authenticationData
    );
    var userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
    var userData = {
      Username: username,
      Pool: userPool,
    };
    CognitoProvider.cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);
    CognitoProvider.cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: function (result: any) {
        var accessToken = result.getAccessToken().getJwtToken();

        CognitoProvider.isAuthenticated = true;
        callback({
          success: true,
          data: {
            token: accessToken
          }
        });
      },

      onFailure: function (err: any) {
        console.error(err.message || JSON.stringify(err));

        callback({
          success: false,
          data: {
            error: err
          }
        });
      },
    });
  },

  signout(callback: VoidFunction) {
    var userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
    userPool.getCurrentUser()?.signOut(() => {
      CognitoProvider.isAuthenticated = false;
      callback();
    });
  },

  /**
   * Register a new, unactivated user
   */
  signup(username: string, password: string, callback: any) {
    const cognitoClient = new CognitoIdentityProviderClient({region: "us-west-2"});

    const dataEmail = {
      Name: 'email',
      Value: username,
    };

    const command = new SignUpCommand({
      ClientId: poolData.ClientId,
      Username: username,
      Password: password,
      UserAttributes: [dataEmail],
    });


    cognitoClient.send(command).then(result => {
      callback({ success: true, data: result });
    }).catch((err => {
      callback({ success: false, data: { err } });
      return;
    }));
  },

  forgotPassword(username: string | null, callback: any) {
    var localCognitoUser = CognitoProvider.cognitoUser;
    if (localCognitoUser == null && username != null) {
      var userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
      var userData = {
        Username: username,
        Pool: userPool,
      };
      localCognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);
    }

    // call forgotPassword on cognitoUser
    localCognitoUser?.forgotPassword({
      onSuccess: function (result) {
        console.log('call result: ' + result);
        callback({
          success: true
        });
      },
      onFailure: function (err) {
        alert(err);
        callback({
          success: false
        });
      }
    });
  },

  confirmPassword(username: string, verificationCode: string, newPassword: string, callback: any) {
    var localCognitoUser = CognitoProvider.cognitoUser;
    if (localCognitoUser == null) {
      var userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
      var userData = {
        Username: username,
        Pool: userPool,
      };
      localCognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);
    }

    localCognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: function (result) {
        console.log('call result: ' + result);
        callback({
          success: true
        });
      },
      onFailure: function (err) {
        alert(err);
        callback({
          success: false
        });
      }
    });
  },

  /**
   * Activate the account with a confirmation code that gets sent to the user's email address
   */
  verify(username: string, code: string, session: string, callback: any) {
    const cognitoClient = new CognitoIdentityProviderClient({region: "us-west-2"});

    const command = new ConfirmSignUpCommand({
      ClientId: poolData.ClientId,
      Username: username,
      ConfirmationCode: code,
      Session: session
    });

    cognitoClient.send(command).then(result => {
      callback({ success: true, data: result });
    }).catch((err => {
      callback({ success: false, data: { err } });
      return;
    }));
  },

  signinAfterVerify(username: string, password: string, session: string, callback: any) {
    const cognitoClient = new CognitoIdentityProviderClient({region: "us-west-2"});

    const command = new InitiateAuthCommand({
      ClientId: poolData.ClientId,
      AuthFlow: AuthFlowType.USER_AUTH,
      
      AuthParameters: {
        USERNAME: username,
        PASSWORD: password
      },
      Session: session,
    });

    cognitoClient.send(command).then(result => {
      const challengeCommand = new RespondToAuthChallengeCommand({
        ClientId: poolData.ClientId,
        ChallengeName: "SELECT_CHALLENGE",
        Session: result.Session,
        ChallengeResponses: {
          ANSWER: "PASSWORD",
          USERNAME: username,
          PASSWORD: password,
        },
      });

      cognitoClient.send(challengeCommand).then(result => {
        callback({success: true, data: result});
      }).catch((err => {
        callback({ success: false, data: { err } });
        return;
      }));

    }).catch((err => {
      console.error("Initial auth failed: ", err);
      callback({ success: false, data: { err } });
      return;
    }));
  },

  getAccessToken(): Promise<CognitoAccessToken | null> {
    var userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
    const currentUser = userPool.getCurrentUser();

    return new Promise((resolve, reject) => {
      if (currentUser === null) {
        reject();
        return;
      }

      currentUser.getSession(function (err: Error, session: any) {
        if (err) {
          alert(err.message || JSON.stringify(err));
          reject(err);
          return;
        }

        if (session.isValid()) {
          resolve(session.getAccessToken().getJwtToken());
        }
      });
    });
  },

  getSession(): Promise<CognitoUserSession | null> {
    var userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
    const cognitoUser = userPool.getCurrentUser();
    return new Promise((resolve, reject) => {
      if (!cognitoUser) {
        reject(new Error("No user found"));
        return;
      }
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(session);
      });
    });
  },

  /**
   * Resend Verification Code
   */
  resendVerificationCode(username: string, callback: any) {
    // Todo
  },

  changePassword(oldPassword: string, newPassword: string, callback: (success: boolean, error?: string) => void) {
    const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      // Needs to get a session first to change the password
      currentUser.getSession((err: Error | null) => {
        if (err) {
          callback(false, err?.message);
          return;
        }
        else {
          currentUser.changePassword(oldPassword, newPassword, (err?: Error | undefined, result?: "SUCCESS" | undefined) => {
            if (result) {
              callback(true);
            } else {
              callback(false, err?.message);
            }
          });
        }
      });
    }
    else {
      callback(false, "Can not find the current user");
    }
  },
};

export { CognitoProvider };
